import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquareMinus,
  faSquarePlus,
} from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import orderBy from "lodash/orderBy";

import Badge from "components/Badge";
import Button from "components/Button";
import Grid, { type GridColumn, NumberCell } from "components/Grid";
import { type SortOrder } from "components/Grid/util";
import {
  ServerStatusType,
  StatusLayerGroupIcon,
  StatusLayerIcon,
} from "components/Icons";
import Loading from "components/Loading";
import Panel from "components/Panel";
import { useQueryStringSort } from "utils/hooks";
import { useRoutes } from "utils/routes";

import {
  OrganizationOverviewServerGroupTableQuery,
  useOrganizationOverviewServerGroupTableQuery,
} from "./gql/Query.generated";

type ServerGroupSummary =
  OrganizationOverviewServerGroupTableQuery["getSummaryStatsByServerGroup"];
type SummaryStat = ServerGroupSummary[number];
type ServerSummary = SummaryStat["servers"][number];

type IssueCounts = Pick<
  SummaryStat,
  "databaseCount" | "warningsCount" | "criticalsCount"
>;
type ClusterCounts = IssueCounts & Pick<SummaryStat, "serverCount">;

type ClusterRow = ClusterCounts & {
  kind: "cluster";
  rowKey: string;
  serverGroupId: string;
  clusterId: string;
  serverGroupName: string;
  primaryServerHumanId: string | null;
  isExpanded: boolean;
  servers: ServerSummary[];
};

type ServerRow = IssueCounts & {
  kind: "server";
  rowKey: string;
  serverGroupId: null;
  clusterId: null;
  serverGroupName: null;
  primaryServerHumanId: null;
  serverCount: null;
  serverHumanId: string;
  serverName: string;
  isPrimary: boolean | null;
  isLastChild: boolean;
  hasRecentSnapshot: boolean;
};

type RowData = ClusterRow | ServerRow;

const ServerGroupTable = ({
  organizationSlug,
}: {
  organizationSlug: string;
}) => {
  const { data, loading, error } = useOrganizationOverviewServerGroupTableQuery(
    { variables: { organizationSlug } },
  );
  if (loading || error) {
    return <Loading error={!!error} />;
  }
  return <TableContent data={data?.getSummaryStatsByServerGroup || []} />;
};

const TableContent = ({ data }: { data: ServerGroupSummary }) => {
  const [sort, setSort] = useQueryStringSort<keyof ClusterRow>({
    param: "sort",
    sortBy: "serverGroupName",
    sortDirection: "ASC",
  });
  const hasActiveClusters = data.some(
    (s) => !isClusterStale(s) && s.serverCount >= 1,
  );
  const [showInactive, setShowInactive] = useState(!hasActiveClusters);
  const visibleData = useMemo(
    () => (showInactive ? data : data.filter((s) => !isClusterStale(s))),
    [data, showInactive],
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggleExpand = (clusterId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(clusterId)) {
        next.delete(clusterId);
      } else {
        next.add(clusterId);
      }
      return next;
    });
  };

  const rows: RowData[] = useMemo(() => {
    const clusterRows: ClusterRow[] = visibleData.map(
      (summary: SummaryStat): ClusterRow => {
        const { serverGroup } = summary;
        return {
          kind: "cluster",
          rowKey: `cluster:${serverGroup.id}`,
          serverGroupId: serverGroup.id,
          clusterId: serverGroup.clusterId,
          serverGroupName: serverGroup.name ?? serverGroup.clusterId,
          primaryServerHumanId: serverGroup.primaryServerHumanId ?? null,
          serverCount: summary.serverCount,
          databaseCount: summary.databaseCount,
          warningsCount: summary.warningsCount,
          criticalsCount: summary.criticalsCount,
          isExpanded: !collapsed.has(serverGroup.clusterId),
          servers: summary.servers,
        };
      },
    );
    const order = sort.sortDirection === "ASC" ? "asc" : "desc";
    const sortedClusters =
      sort.sortBy === "serverGroupName"
        ? orderBy(clusterRows, [sort.sortBy], [order])
        : orderBy(
            clusterRows,
            [sort.sortBy, "serverGroupName"],
            [order, "asc"],
          );
    return sortedClusters.flatMap((cluster): RowData[] => {
      if (!cluster.isExpanded) {
        return [cluster];
      }
      const sortedServers = sortPrimaryFirst(cluster.servers);
      return [
        cluster,
        ...sortedServers.map((s, i) =>
          toServerRow(cluster, s, i === sortedServers.length - 1),
        ),
      ];
    });
  }, [visibleData, collapsed, sort.sortBy, sort.sortDirection]);

  const handleSort = (field: keyof RowData, order: SortOrder) => {
    setSort({ sortBy: field as keyof ClusterRow, sortDirection: order });
  };

  const columns: GridColumn<RowData, keyof RowData>[] = [
    {
      field: "rowKey",
      header: "",
      width: "20px",
      disableSort: true,
      renderer: function CaretCell({ rowData }) {
        if (rowData.kind !== "cluster") {
          return null;
        }
        if ((rowData.serverCount ?? 0) < 1) {
          return null;
        }
        return (
          <span className="relative inline-flex items-center">
            <Button
              bare
              className="translate-y-[-1px]"
              aria-label={`Expand ${rowData.serverGroupName}`}
              aria-expanded={rowData.isExpanded}
              onClick={() => toggleExpand(rowData.clusterId)}
            >
              <FontAwesomeIcon
                icon={rowData.isExpanded ? faSquareMinus : faSquarePlus}
                className="text-gray-500 text-[10px]"
              />
            </Button>
            {/* Vertical tree line that connects the cluster to its members */}
            {rowData.isExpanded && (
              <span className="absolute left-[4px] top-full bottom-[-5px] w-px bg-[#CCC]" />
            )}
          </span>
        );
      },
    },
    {
      field: "serverGroupName",
      header: "Server Group",
      width: "minmax(30%,1fr)",
      renderer: NameCell,
      // Allow the server row's tree connector lines to render outside the cell's bounds.
      className: ({ rowData }) =>
        rowData.kind === "server" ? "!overflow-visible" : "",
    },
    {
      field: "databaseCount",
      header: "# of Databases",
      width: "minmax(10%,140px)",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "",
      renderer: NumberCell,
    },
    {
      field: "warningsCount",
      header: "Warnings",
      width: "minmax(10%,140px)",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "",
      renderer: NumberCell,
    },
    {
      field: "criticalsCount",
      header: "Criticals",
      width: "minmax(10%,140px)",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "",
      renderer: NumberCell,
    },
  ];

  const titleSearch = (
    <div className="flex">
      <input
        type="checkbox"
        checked={showInactive}
        id="show_inactive_clusters"
        onChange={(evt) => setShowInactive(evt.target.checked)}
      />
      <label htmlFor="show_inactive_clusters">
        Show server groups with no recent statistics data
      </label>
    </div>
  );

  return (
    <Panel title="All Server Groups" secondaryTitle={titleSearch}>
      <Grid
        striped
        data={rows}
        columns={columns}
        sortedBy={sort.sortBy as keyof RowData}
        sortedOrder={sort.sortDirection}
        handleSort={handleSort}
      />
    </Panel>
  );
};

function NameCell({ rowData }: { rowData: RowData }) {
  const { server, serverGroupServers } = useRoutes();
  switch (rowData.kind) {
    case "cluster": {
      return (
        <span className="flex items-center">
          <StatusLayerGroupIcon status={clusterStatus(rowData.servers)} />
          {rowData.servers.length === 0 ? (
            <span className="ml-[4px]">{rowData.serverGroupName}</span>
          ) : (
            <Link
              className="ml-[4px]"
              to={serverGroupServers(rowData.serverGroupId)}
            >
              {rowData.serverGroupName}
            </Link>
          )}
        </span>
      );
    }
    case "server":
      return (
        <span className="flex items-center">
          <span className="relative w-[12px] mr-[6px] shrink-0 self-stretch">
            <span
              className={classNames(
                "absolute left-[-16px] w-px bg-[#CCC]",
                rowData.isLastChild
                  ? "top-[-5px] h-[calc(50%+5px)]"
                  : "top-[-5px] bottom-[-5px]",
              )}
            />
            <span className="absolute left-[-16px] top-1/2 right-1 h-px bg-[#CCC]" />
          </span>
          <StatusLayerIcon status={serverStatus(rowData)} />
          <Link className="ml-[4px]" to={server(rowData.serverHumanId)}>
            {rowData.serverName}
          </Link>
          {rowData.isPrimary != null && (
            <Badge
              className={classNames(
                "ml-[6px] border font-medium",
                !rowData.isPrimary &&
                  "!bg-[#f0f0f0] !text-[#555] !border-[#ddd]",
              )}
            >
              {rowData.isPrimary ? "primary" : "replica"}
            </Badge>
          )}
        </span>
      );
  }
}

function isClusterStale(summary: SummaryStat): boolean {
  if (summary.servers.length === 0) {
    return true;
  }
  return summary.servers.every((s) => s.latestStats == null);
}

function sortPrimaryFirst(servers: ServerSummary[]): ServerSummary[] {
  return [...servers].sort((a, b) => {
    const ap = a.latestStats?.isPrimary ? 0 : 1;
    const bp = b.latestStats?.isPrimary ? 0 : 1;
    if (ap !== bp) {
      return ap - bp;
    }
    return a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());
  });
}

function toServerRow(
  parent: ClusterRow,
  server: ServerSummary,
  isLastChild: boolean,
): ServerRow {
  return {
    kind: "server",
    rowKey: `server:${parent.serverGroupId}:${server.id}`,
    serverGroupId: null,
    clusterId: null,
    serverGroupName: null,
    primaryServerHumanId: null,
    serverCount: null,
    databaseCount: server.databaseCount,
    warningsCount: server.warningsCount,
    criticalsCount: server.criticalsCount,
    serverHumanId: server.humanId,
    serverName: server.name,
    isPrimary: server.latestStats?.isPrimary ?? null,
    isLastChild,
    hasRecentSnapshot: server.latestStats != null,
  };
}

function clusterStatus(servers: ServerSummary[]): ServerStatusType {
  if (servers.some((s) => s.latestStats != null && s.criticalsCount > 0)) {
    return "critical";
  }
  if (servers.length > 0 && servers.every((s) => s.latestStats != null)) {
    return "okay";
  }
  return "no data";
}

function serverStatus(row: ServerRow): ServerStatusType {
  if (!row.hasRecentSnapshot) {
    return "no data";
  }
  return row.criticalsCount > 0 ? "critical" : "okay";
}

export default ServerGroupTable;
